import {
  ChangeEvent as ReactChangeEvent,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { SYM } from '../../Components/MaterialSymbol';
import { Form } from '../../Components/Form';
import { UserMessage } from '../../../common/Types';
import marked from 'marked';
import { getEasyLayout } from '@resistdesign/voltra/app/utils';
import { AiMessageReader } from './AiMessageReader';
import { SpeechToText } from './SpeechToText';

const { parse: parseMarkdown } = marked;

const LayoutBase = styled.div`
  flex: 1 0 auto;
  width: 100%;
  height: 100%;
  gap: 1em;
  overflow: hidden;
`;
const MessageItem = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: stretch;
  margin-bottom: 1em;
`;
const MessageText = styled.div`
  padding-top: 0.5em;
`;
const {
  layout: Layout,
  areas: { Chat, Reply },
} = getEasyLayout(LayoutBase)`
  chat, 12fr
  reply, 1fr
  \\ 1fr
`;
const ChatLayout = styled(Chat)`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: auto;

  & > :first-child {
    margin-top: auto;
  }
`;
const ReplyInput = styled.input.attrs(() => ({
  className: 'reply-input',
}))`
  &.reply-input {
    flex: 1 0 auto;
    height: auto;
    margin-bottom: 0;
  }
`;
const ReplyInputPlaceholder = styled.div`
  flex: 1 0 auto;
  width: 75%;
  height: auto;
`;
const AudioQuestionInfo = styled.article`
  padding: 0.5em;
  margin: 0.5em 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1em;
`;

type MessageTextDisplayProps = PropsWithChildren;

const MessageTextDisplay: FC<MessageTextDisplayProps> = ({ children }: MessageTextDisplayProps) => {
  const renderedHTML = useMemo(() => (typeof children === 'string' ? parseMarkdown(children) : undefined), [children]);

  return renderedHTML ? (
    <MessageText
      dangerouslySetInnerHTML={{
        __html: renderedHTML,
      }}
    />
  ) : (
    <MessageText>{children}</MessageText>
  );
};

export type InterviewChatFormProps = PropsWithChildren<{
  disabled?: boolean;
  messagesLoading?: boolean;
  messages: UserMessage[];
  onAddMessage: (message: string) => void;
}>;

export const InterviewChatForm: FC<InterviewChatFormProps> = ({
  disabled = false,
  messagesLoading = false,
  messages = [],
  onAddMessage,
}: InterviewChatFormProps) => {
  const chatLayoutRef = useRef<HTMLDivElement>(null);
  const [alwaysShowLatest, setAlwaysShowLatest] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const onMessageChange = useCallback(
    ({ target: { value: newMessage = '' } }: ReactChangeEvent<HTMLInputElement>) => setNewMessage(newMessage),
    [setNewMessage]
  );
  const onSubmit = useCallback(() => {
    if (newMessage) {
      onAddMessage?.(newMessage);
      setNewMessage('');
    }
  }, [onAddMessage, newMessage]);
  const toggleAlwaysShowLatest = useCallback(() => setAlwaysShowLatest((alwaysShowLatest) => !alwaysShowLatest), []);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const onTextReceived = useCallback(
    (text: string) => {
      setNewMessage(text);
    },
    [setNewMessage]
  );
  const disableListening = useMemo(() => speaking || messagesLoading, [speaking, messagesLoading]);
  const onGoToAudioRecording = useCallback(async () => {
    history.pushState({}, '', '/interview/audio');
  }, []);

  useEffect(() => {
    if (alwaysShowLatest && chatLayoutRef.current) {
      chatLayoutRef.current.scrollTop = chatLayoutRef.current.scrollHeight;
    }
  }, [messagesLoading, alwaysShowLatest]);

  useEffect(() => {
    if (!speaking && listening && typeof newMessage === 'string' && newMessage.length > 3) {
      const tId = setTimeout(() => {
        onSubmit();
      }, 2000);

      return () => {
        clearTimeout(tId);
      };
    }
  }, [speaking, listening, newMessage, onSubmit]);

  return (
    <Layout>
      <ChatLayout
        {...({
          ref: chatLayoutRef,
        } as any)}
      >
        {messages.map((m, index) => {
          const { role = 'user', content = '' } = m;

          return (
            <MessageItem key={`MessageItem:${index}`}>
              <SYM
                style={{
                  margin: '0.5em',
                }}
              >
                {role === 'user' ? 'person' : 'memory'}
              </SYM>
              <MessageTextDisplay>{content}</MessageTextDisplay>
            </MessageItem>
          );
        })}
        {messagesLoading ? (
          <MessageItem>
            <SYM
              style={{
                margin: '0.5em',
              }}
            >
              memory
            </SYM>
            <MessageTextDisplay>
              <article aria-busy="true"></article>
            </MessageTextDisplay>
          </MessageItem>
        ) : undefined}
      </ChatLayout>
      <Reply>
        {disabled ? (
          <AudioQuestionInfo>
            Provide an audio sample to complete your interview. <button onClick={onGoToAudioRecording}>Next</button>
          </AudioQuestionInfo>
        ) : (
          <Form role="group" onSubmit={onSubmit}>
            <button type="button" title="Always Show Latest" onClick={toggleAlwaysShowLatest}>
              <SYM
                style={{
                  opacity: alwaysShowLatest ? 1 : 0.5,
                }}
              >
                arrow_downward
              </SYM>
            </button>
            <AiMessageReader messages={messages} onSpeakingChange={setSpeaking} />
            <ReplyInput disabled={listening} type="text" value={newMessage} onChange={onMessageChange} />
            <SpeechToText
              disabled={disableListening}
              onTextReceived={onTextReceived}
              onListeningChange={setListening}
            />
            <button disabled={listening} type="submit">
              <SYM>send</SYM>
            </button>
          </Form>
        )}
      </Reply>
    </Layout>
  );
};
